// components/charts/CustomBarChart.jsx
import React from "react";
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import { ThemeProvider } from "@mui/material";
import NoDataMessage from "../../components/NoDataMessage/NoDataMessage.jsx";

const CustomBarChart = ({
  data,
  bars = [], // [{ dataKey: string, name: string, fill: string, radius?: [number,number,number,number] }]
  xKey = "name",
  height = 300,
  width, // if passed → fixed width (for modal), else responsive
  margin = { top: 20, right: 30, left: 20, bottom: 5 },
  onBarClick,
  xAngle = 0,
  xTextAnchor = "middle",
  xHeight = 60,
  showGrid = true,
  tooltipLabelFormatter,
  chartTheme,
  className = "",
}) => {

  const hasData =
    data &&
    data.length > 0 &&
    data.some((d) => bars.some((b) => (d[b.dataKey] ?? 0) > 0));

  if (!hasData) {
    return (
      <div
        className={`flex items-center justify-center h-[${height}px] ${className}`}
      >
        <NoDataMessage />
      </div>
    );
  }

  const Container = width ? "div" : ResponsiveContainer;

  const containerProps = width
    ? { style: { width: width + "px", height: height + "px" } }
    : { width: "100%", height };

  return (
    <ThemeProvider theme={chartTheme}>
      <Container {...containerProps} className={className}>
        <RechartsBarChart data={data} margin={margin} onClick={onBarClick}>
          {/* GRID */}
          {showGrid && (
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="rgba(255, 255, 255, 0.1)"
            />
          )}

          {/* X AXIS */}
          <XAxis
            dataKey={xKey}
            tick={{ fill: "#d3d3d3", fontSize: 12 }}
            axisLine={{ stroke: "#4f4f79" }}
            tickLine={{ stroke: "#4f4f79" }}
            angle={xAngle}
            textAnchor={xTextAnchor}
            height={xHeight}
            interval={0}
          />

          {/* Y AXIS */}
          <YAxis
            tick={{ fill: "#d3d3d3", fontSize: 12 }}
            axisLine={{ stroke: "#4f4f79" }}
            tickLine={{ stroke: "#4f4f79" }}
          />

          {/* TOOLTIP */}
          <Tooltip
            cursor={{ stroke: "#B794FF", strokeWidth: 2, strokeDasharray: "6 6" }}
            contentStyle={{
              background:
                "linear-gradient(135deg, rgba(0, 0, 0, 0.69) 0%, rgba(0, 0, 0, 0.69) 100%)",
              border: "1px solid rgba(0, 0, 0, 0.6)",
              borderRadius: "8px",
              padding: "12px 16px",
              backdropFilter: "blur(12px)",
            }}
            itemStyle={{ color: "#E8D0FF", fontWeight: 600, fontSize: "14px" }}
            labelStyle={{
              color: "#ffffff",
              fontWeight: 500,
              fontSize: "15px",
              marginBottom: "8px",
            }}
            formatter={(value) => (
              <span style={{ color: "#FFFFFF", fontWeight: 700 }}>{value}</span>
            )}
            labelFormatter={
              tooltipLabelFormatter ||
              ((label) => String(label).toUpperCase())
            }
          />

          {/* BARS + LABELS */}
          {bars.map((bar) => (
            <Bar
              key={bar.dataKey}
              dataKey={bar.dataKey}
              name={bar.name || bar.dataKey}
              fill={bar.fill}
              radius={bar.radius || [4, 4, 0, 0]}
              onClick={onBarClick}
              cursor={onBarClick ? "pointer" : "default"}
            >
              {/* Only show label for trial bar */}
              {bar.dataKey === "Trial" && (
                <LabelList
                  dataKey={xKey}
                  position="bottom"
                  offset={10}
                  style={{
                    fill: "#E0E0FF",
                    fontSize: 12,
                    fontWeight: 400,
                  }}
                />
              )}
            </Bar>
          ))}
        </RechartsBarChart>
      </Container>
    </ThemeProvider>
  );
};

export default CustomBarChart;
